/**
 * Compute 4 fun "connection" percentages from a session's state.
 * Deterministic given the same inputs so re-renders don't flicker.
 *
 * Returns { chemistry, chaos, toxic, soulmate } — each 0..100.
 *
 * Heuristics (vibes, not science 😄):
 *   chemistry  → small score gap + enough rounds played → higher
 *   chaos      → rounds × spice weight + dare ratio → higher
 *   soulmate   → blend of chemistry × engagement, with a cute floor
 *   toxic      → inverse of soulmate, capped (keep it fun, never mean)
 */
export function computeConnectionScore({
  hostScore = 0,
  guestScore = 0,
  roundsPlayed = 0,
  spiceLevel = "flirty",
  dareRatio = 0.5, // 0..1
  modeLabel = "",
} = {}) {
  const totalScore = Math.max(hostScore + guestScore, 1);
  const gap = Math.abs(hostScore - guestScore);
  const rounds = Math.max(roundsPlayed, hostScore + guestScore);
  const spiceWeight = { cute: 0.55, flirty: 0.75, spicy: 1.0 }[spiceLevel] ?? 0.7;
  const deepMode = /deep|partner quiz|know me/i.test(modeLabel) ? 0.12 : 0;

  // Chemistry: tight score gap + enough rounds
  const gapRatio = 1 - Math.min(gap / totalScore, 1);
  const engagement = Math.min(rounds / 10, 1); // saturates at 10 rounds
  let chemistry = 55 + gapRatio * 32 + engagement * 10 + deepMode * 100;
  chemistry = clamp(chemistry, 55, 99);

  // Chaos: rounds × spice + some dare ratio
  let chaos = 35 + engagement * 30 + spiceWeight * 25 + dareRatio * 12;
  chaos = clamp(chaos, 35, 99);

  // Soulmate: blends chemistry with engagement, with floor
  let soulmate = 0.6 * chemistry + 0.4 * (60 + engagement * 35);
  soulmate = clamp(soulmate, 60, 99);

  // Toxic: deliberate low cap so it's playful, never cruel.
  let toxic = clamp(100 - soulmate - 18 + chaos * 0.15, 4, 42);

  return {
    chemistry: Math.round(chemistry),
    chaos: Math.round(chaos),
    toxic: Math.round(toxic),
    soulmate: Math.round(soulmate),
  };
}

// Fun one-liner to go with a set of scores.
export function connectionVibeLabel({ chemistry, chaos, toxic, soulmate }) {
  if (soulmate >= 92) return "Unreal sync — someone call Hinge 💍";
  if (chaos >= 88) return "Certified menace behaviour 🔥";
  if (chemistry >= 88) return "The vibes are vibing 💫";
  if (toxic >= 35) return "A little spicy… we love it 🌶️";
  if (chemistry >= 75) return "You two are on to something 👀";
  return "Just getting warmed up 😈";
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}
